/**
 * Core icon library:
 *
 * https://www.figma.com/file/zYke5qihweC8yQSpXpcRO2/%F0%9F%93%9000-OM-CORE-Library?node-id=17%3A175
 */

// ============================================================================
// README
// ----------------------------------------------------------------------------
// The following objects are taken directly from the CORE figma diagram.
// No additions or changes should be made here, except to align with the CORE
// diagram.
// Changes to the CORE diagram should go through the process defined in RFC009.
// ============================================================================

import React, { useContext } from 'react';
import styled from '@emotion/styled';
import { ColorCodes } from '../color';
import svgs, { SvgNames, SvgReference, SVG_SIZES } from './svg';
import { Theme, ThemeContext } from '../../utilities/theme';

export type IconProps = {
  transparent?: boolean;
  /** Naming is based off the schema described in the figma file, generally falling into the category sizeName or sizeSubsectionName */
  name: SvgNames;
  /** A title to help with accessibility and to provide a tooltip. */
  accessibleTitle?: string;
} & (
  | {
      /** Whether to take color from existing css properties */
      inheritFill: boolean;
      /** A color or selection of colors to use for the icon. */
      fill?: undefined;
    }
  | {
      fill: ColorCodes | { [key in Theme]?: string };
      inheritFill?: undefined | false;
    }
  | {
      fill?: undefined;
      inheritFill?: undefined | false;
    }
);

const IconWrapper = styled('div')<{ fill?: string }>(
  p =>
    p.fill
      ? {
          'svg *': {
            fill: p.fill,
          },
        }
      : {},
  {
    svg: {
      // svg element must be set to display block, otherwise the IconWrapper
      // will end up slightly taller than the icon, creating a bit of difficulty
      // aligning the icon in practice
      display: 'block',
    },
  }
);

const extractColor = (
  { fill, inheritFill: inheritColor }: IconProps,
  defaultColor: SvgReference['defaultColor'],
  theme: Theme
) => {
  if (inheritColor) {
    return undefined;
  }
  if (typeof fill === 'string') {
    return fill;
  }

  if (fill && fill[theme]) {
    return fill[theme];
  }
  switch (theme) {
    case 'futuro-dark':
      return defaultColor?.DARK;
    case 'futuro':
    default:
      return defaultColor?.LIGHT;
  }
};

/**
 * A predefined futuro icon.
 * @deprecated Use Lucide icons instead.
 * @argument name string - the name of the icon - see below for details
 * @argument inheritFill boolean - whether to take color from parent css elements
 *   defaults to false
 * @argument fill ColorCode - another color to use for the icon, or a selection from the themes.
 * @argument accessibleTitle string - for use when the icon is a standalone action.
 *   If provided, adds a title element to the svg element.
 *
 * Icon naming is based off the sizes in the figma file:
 *
 * https://www.figma.com/file/zYke5qihweC8yQSpXpcRO2/%F0%9F%93%9000-OM-CORE-Library?node-id=17%3A175
 *
 * "sizeName"
 * "sizeSubsectionName"
 *
 * By default, the color is set according to the theme, and what is used in
 *  the figma documentation. This can be modified in two ways.
 *   - By setting inheritColor, no color properties will be added to the icon
 *     so it will inherit the fill from a parent element targeting the .svg-fill
 *     class.
 *   - By setting color, a single hex code can be provided, allowing custom
 *     colors to be set for the icon. Alternatively, an object of themes can be
 *     provided allowing a range of colors to be set dependent on the theme.
 *
 * Accessible title should be used in cases where the icon is unaccompanied by
 * visible text or a link, and should provide some meaningful information about
 * what interacting with the icon does. See here for more details:
 * https://css-tricks.com/accessible-svg-icons/
 *
 * @example
 *
 * <Icon name="sActionsEdit" />
 * <Icon name="sActionsEdit" fill={C.LIGHT.SWEET_ORANGE} />
 * <Icon name="sActionsEdit" fill={{
 *   futuro: C.LIGHT.SWEET_ORANGE,
 *   'futuro-dark': C.DARK.SWEET_ORANGE,
 *   standard: '#DEDEDE',
 * }} />
 * <Icon name="sActionsEdit" inheritFill />
 * <Icon name="sActionsEdit" accessibleTitle="Edit Product" />
 */
export const Icon = (props: IconProps) => {
  const { name, transparent = false } = props;

  const theme = useContext(ThemeContext);

  const result = svgs[name];

  if (!result) {
    return null;
  }

  const { svg, viewBox, defaultColor, size } = result;
  const fill = extractColor(props, defaultColor, theme);
  const sizeProps = SVG_SIZES[size];
  const a11y = !!props.accessibleTitle;
  return (
    <IconWrapper fill={fill} data-name="icon-wrapper">
      <svg
        {...sizeProps}
        viewBox={viewBox}
        fill={!transparent ? 'currentColor' : 'transparent'}
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden={!a11y}
        role={a11y ? 'img' : undefined}
      >
        {a11y && <title>{props.accessibleTitle}</title>}
        {svg}
      </svg>
    </IconWrapper>
  );
};

export const iconNames = Object.keys(svgs);

export default Icon;
